import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const deliveryLocationSeasonalityCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'DeliveryLocationSeasonality',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.deliveryLocationSeasonalities.`,
});

export async function getDeliveryLocationSeasonalities(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = deliveryLocationSeasonalityCache.getFromMemory('all');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('SEASONALITIES');
			if (legacyData) {
				log('info', 'DeliveryLocationSeasonality', '📦 Seeding Vue cache from legacy storage (SEASONALITIES)');
				await deliveryLocationSeasonalityCache.put('all', legacyData);
				deliveryLocationSeasonalityCache.setInMemory('all', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'DeliveryLocationSeasonality', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	try {
		const cached = await deliveryLocationSeasonalityCache.get('all');
		if (cached && !forceRefresh) {
			deliveryLocationSeasonalityCache.setInMemory('all', cached);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'DeliveryLocationSeasonality', '📴 Offline: Returning cached delivery location seasonalities');
				return cached;
			}

			setTimeout(() => refreshDeliveryLocationSeasonalitiesInBackground(), 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'DeliveryLocationSeasonality', '📴 Offline: No cached delivery location seasonalities available');
			return [];
		}

		return await fetchDeliveryLocationSeasonalitiesFromAPI();
	} catch (error) {
		log('error', 'DeliveryLocationSeasonality', `❌ Error getting delivery location seasonalities: ${error.message}`);

		try {
			const fallback = await deliveryLocationSeasonalityCache.get('all');
			if (fallback) {
				log('info', 'DeliveryLocationSeasonality', '💾 Using fallback cached delivery location seasonalities');
				return fallback;
			}
		} catch (cacheError) {
			log('error', 'DeliveryLocationSeasonality', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchDeliveryLocationSeasonalitiesFromAPI() {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationSeasonalities'), { method: 'GET' }, 'tokenSales');

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const seasonalities = await response.json();
		await deliveryLocationSeasonalityCache.put('all', seasonalities);
		deliveryLocationSeasonalityCache.setInMemory('all', seasonalities);

		log('success', 'DeliveryLocationSeasonality', `✅ Fetched and cached ${seasonalities.length} delivery location seasonalities`);
		return seasonalities;
	} catch (error) {
		log('error', 'DeliveryLocationSeasonality', `❌ Failed to fetch delivery location seasonalities from API: ${error.message}`);
		throw error;
	}
}

async function refreshDeliveryLocationSeasonalitiesInBackground() {
	try {
		await fetchDeliveryLocationSeasonalitiesFromAPI();
		log('success', 'DeliveryLocationSeasonality', '🔄 Background refresh of delivery location seasonalities completed');
	} catch (error) {
		log('warn', 'DeliveryLocationSeasonality', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshDeliveryLocationSeasonalities() {
	deliveryLocationSeasonalityCache.clearMemory();
	await deliveryLocationSeasonalityCache.clear();
}
